/* @flow */

import * as React from 'react';
import { find, head, get, values } from 'lodash/fp';
import moment from 'moment';

import SimpleSticky from 'app/components/commons/SimpleSticky';
import t from 'app/utils/translate';
import Button from 'app/components/commons/Button';
import MaxedFixturePlayersInfo from 'app/components/challenge/MaxedFixturePlayersInfo';
import MaxedSidesInfo from 'app/components/challenge/MaxedSidesInfo';
import PlayerPositionsBar from 'app/components/challenge/PlayerPositionsBar';
import PlayerSelectionFilterBar from 'app/components/challenge/PlayerSelectionFilterBar';
import PlayerSelectionTable from 'app/components/challenge/PlayerSelectionTable';
import PlayerSelectionTableHead from 'app/components/challenge/PlayerSelectionTableHead';
import Responsive from 'app/components/responsive/Responsive';
import SalaryStatus from 'app/components/challenge/SalaryStatus';
import PlayerGamesBar from './PlayerGamesBar';
import GameInfo from './GameInfo';
import ProjectedRacingScore from './ProjectedRacingScore';
import RacingScoresDescription from './RacingScoresDescription';

type Props = {
  autoAdvancePositions?: boolean,
  disabled?: boolean,
  displayWidget?: boolean,
  games?: Array<*>,
  gameFilter?: string,
  isAnimating?: boolean,
  isDesktop?: boolean,
  isRacing: boolean,
  showAllPositionsFilter?: boolean,
  offsetTop?: number,
  onAdd: Object => void,
  onGameSelect?: string => void,
  onPositionSelect: string => void,
  onRemove: string => void,
  onReview: string => void,
  onWidgetClick: string => void,
  players: Array<*>,
  playerSelectionTableInfo: Object,
  positionFilter?: string,
  positions: Array<*>,
  requiredSelections?: number,
  salaryCap?: number,
  salaryExpenditure?: number,
  searchTerm?: string,
  showPlayerSelections: boolean,
  setRef: (id: string, ref: any) => void,
  totalSelected: number,
  widgetPlayerId?: string
};

type State = {
  widgetPlayerId: ?string
};

class PlayerSelection extends React.Component<Props, State> {
  props: Props;

  static defaultProps = {
    autoAdvancePositions: false,
    disabled: false,
    displayWidget: false,
    gameFilter: undefined,
    games: [],
    isAnimating: false,
    showAllPositionsFilter: true,
    isDesktop: false,
    offsetTop: 0,
    onGameSelect: undefined,
    positionFilter: undefined,
    requiredSelections: 0,
    salaryCap: 0,
    salaryExpenditure: 0,
    searchTerm: undefined,
    widgetPlayerId: undefined
  };

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyPress);
  }

  shouldComponentUpdate(nextProps: Props) {
    // Do not update player selection view when animating
    return !nextProps.isAnimating;
  }

  componentDidUpdate(prevProps: Props) {
    const { positionFilter, searchTerm } = this.props;
    // Ensure that switching to a new tab OR
    // finishing searching for a player resets the scroll position to the top of the list
    if (positionFilter !== prevProps.positionFilter || (!searchTerm && prevProps.searchTerm)) {
      this.playersRef.scrollIntoView(true);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyPress);
  }

  playerSelectionTableRef: HTMLElement;

  // eslint-disable-line react/sort-comp
  playersRef: HTMLElement;

  handleKeyPress = (e: React.KeyboardEvent<*>) => {
    const { searchTerm, players, onAdd } = this.props;
    const ENTER_KEY = 13;
    if (e.keyCode === ENTER_KEY && searchTerm) {
      // when searching if you press Enter it should add the first result to your lineup
      const player = find(p => !p.disabled, players);
      if (player) {
        onAdd({ id: player.id, idStr: player.idStr, isTeam: player.isTeam }, player.positions);
      }
    }
  };

  setPlayerRef = (node: HTMLElement) => {
    this.playersRef = node;
  };

  render() {
    const {
      autoAdvancePositions,
      disabled,
      displayWidget,
      games,
      gameFilter,
      isAnimating,
      isDesktop,
      isRacing,
      showAllPositionsFilter,
      offsetTop,
      onAdd,
      onGameSelect,
      onPositionSelect,
      onRemove,
      onReview,
      onWidgetClick,
      players,
      playerSelectionTableInfo,
      positionFilter,
      positions,
      requiredSelections,
      salaryCap,
      salaryExpenditure,
      searchTerm,
      showPlayerSelections,
      totalSelected,
      widgetPlayerId,
      setRef
    } = this.props;

    const selectedGame = find({ id: gameFilter }, games);

    return (
      <div ref={this.setPlayerRef}>
        <SimpleSticky isStuckFunc={isStuck => isStuck && !searchTerm} offsetTop={offsetTop}>
          <div>
            {!isRacing ? (
              <React.Fragment>
                <SalaryStatus
                  currentStep={totalSelected}
                  salaryCap={salaryCap}
                  salaryExpenditure={salaryExpenditure}
                  totalSteps={requiredSelections}
                />
                <PlayerPositionsBar
                  autoAdvancePositions={autoAdvancePositions}
                  onSelect={onPositionSelect}
                  positions={positions}
                  requiredSelections={requiredSelections}
                  selectedPosition={positionFilter}
                  totalSelected={totalSelected}
                  showAllTab={showAllPositionsFilter}
                  showSelectionFeedback
                />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <ProjectedRacingScore
                  first={isDesktop}
                  players={get('selections', head(values(positions)))}
                />
                <RacingScoresDescription />
                <PlayerGamesBar
                  autoAdvance={autoAdvancePositions}
                  onSelect={onGameSelect}
                  games={games}
                  isRacing={isRacing}
                  requiredSelections={requiredSelections}
                  selectedGame={gameFilter}
                  totalSelected={totalSelected}
                  showSelectionFeedback={false}
                  showAllTab={false}
                />
              </React.Fragment>
            )}
            {selectedGame && (
              <GameInfo
                distance={selectedGame.distance}
                number={selectedGame.number}
                track={selectedGame.track}
                weather={selectedGame.weather}
                name={selectedGame.name}
                places={selectedGame.places}
                venue={selectedGame.venue}
                startTime={moment(selectedGame.startTime).format('h:mm a')}
              />
            )}
            <PlayerSelectionFilterBar
              isRacing={isRacing}
              positionFilter={get('name', find({ id: positionFilter }, positions))}
              gameFilter={get('name', selectedGame)}
            />
            <Responsive tabletLandscape desktop>
              <PlayerSelectionTableHead playerSelectionTableInfo={playerSelectionTableInfo} />
            </Responsive>
            <MaxedFixturePlayersInfo />
            <MaxedSidesInfo />
          </div>
        </SimpleSticky>
        <PlayerSelectionTable
          innerRef={ref => {
            this.playerSelectionTableRef = ref;
          }}
          displayWidget={displayWidget}
          disabled={disabled}
          isAnimating={isAnimating}
          isRacing={isRacing}
          positionFilter={positionFilter}
          players={players}
          playerSelectionTableInfo={playerSelectionTableInfo}
          onAdd={onAdd}
          onRemove={onRemove}
          onWidgetClick={onWidgetClick}
          salaryCap={salaryCap}
          salaryExpenditure={salaryExpenditure}
          showPlayerSelections={showPlayerSelections}
          widgetPlayerId={widgetPlayerId}
          setRef={setRef}
        />
        <Responsive mobile={totalSelected === requiredSelections} tabletLandscape={false}>
          <Button autoBorderRadius={false} block size="medium" kind="warning" onClick={onReview}>
            {t('Review Team')}
          </Button>
        </Responsive>
      </div>
    );
  }
}

export default PlayerSelection;
